import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ChevronDown,
  LogOut,
  Menu,
  Search,
  ShoppingBag,
  Store,
  UserRound,
  X,
} from "lucide-react";

import { useCustomerAuth } from "../context/CustomerAuthContext";
import { apiRequest } from "../services/api";
import logo from "../assets/logo.png";

const getCartCount = () => {
  try {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    return cart.reduce((total, item) => total + Number(item.quantity || 1), 0);
  } catch {
    return 0;
  }
};

const getImageUrl = (image) => {
  if (!image) return null;

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  const baseUrl = process.env.REACT_APP_ASSET_URL || "";

  return `${baseUrl}${image.startsWith("/") ? image : `/${image}`}`;
};

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);

  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(getCartCount);

  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  const {
    customer,
    isAuthenticated,
    isLoading: isCustomerLoading,
    logout,
  } = useCustomerAuth();

  const navCategories = useMemo(() => categories.slice(0, 5), [categories]);

  const megaCategories = useMemo(() => categories.slice(0, 8), [categories]);

  const mobileCategories = useMemo(() => categories.slice(0, 12), [categories]);

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      try {
        const response = await apiRequest("/storefront/categories");

        if (isMounted) {
          setCategories(Array.isArray(response) ? response : []);
        }
      } catch {
        if (isMounted) {
          setCategories([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingCategories(false);
        }
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const syncCart = () => setCartCount(getCartCount());

    syncCart();

    window.addEventListener("storage", syncCart);
    window.addEventListener("cartUpdated", syncCart);

    return () => {
      window.removeEventListener("storage", syncCart);
      window.removeEventListener("cartUpdated", syncCart);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setProfileOpen(false);
    setMegaMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!searchOpen) return undefined;

    const timeout = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 150);

    return () => clearTimeout(timeout);
  }, [searchOpen]);

  useEffect(() => {
    const onEscape = (event) => {
      if (event.key !== "Escape") return;

      setMenuOpen(false);
      setSearchOpen(false);
      setProfileOpen(false);
      setMegaMenuOpen(false);
    };

    window.addEventListener("keydown", onEscape);

    return () => window.removeEventListener("keydown", onEscape);
  }, []);

  const goTo = (path) => {
    navigate(path);
    setMenuOpen(false);
    setSearchOpen(false);
    setProfileOpen(false);
    setMegaMenuOpen(false);
  };

  const handleSearch = () => {
    const query = search.trim();

    if (!query) return;

    goTo(`/search/${encodeURIComponent(query)}`);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      goTo("/");
    }
  };

  const categoryPath = (category) =>
    `/category/${category.slug || category.id}`;

  const isCategoryActive = (category) =>
    location.pathname === categoryPath(category);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-[#e8dfdb] bg-[#fcfbfa]">
        {/* Editorial utility strip */}
        <div className="hidden bg-[#250c12] lg:block">
          <div className="mx-auto flex h-9 max-w-[1536px] items-center justify-between px-8 xl:px-12">
            <p className="text-[9px] font-bold uppercase tracking-[0.24em] text-white/65">
              CHARMS · Curated fashion, consciously chosen
            </p>

            <div className="flex items-center gap-7">
              <button
                type="button"
                onClick={() => goTo("/search/all?sort=newest")}
                className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/80 transition hover:text-white"
              >
                New arrivals
              </button>

              <button
                type="button"
                onClick={() => goTo("/search/all")}
                className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/80 transition hover:text-white"
              >
                Our stores
              </button>
            </div>
          </div>
        </div>

        {/* Main wordmark header */}
        <div className="relative mx-auto flex h-[74px] max-w-[1536px] items-center px-4 sm:px-7 lg:h-[96px] lg:px-12">
          <div className="hidden min-w-[260px] items-center gap-7 lg:flex">
            <button
              type="button"
              onClick={() => goTo("/search/all")}
              className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-800 transition hover:text-primary-800"
            >
              Shop all
            </button>

            <button
              type="button"
              onClick={() => goTo("/gift-cards")}
              className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 transition hover:text-primary-800"
            >
              Gift cards
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setMenuOpen((current) => !current);
              setProfileOpen(false);
              setSearchOpen(false);
              setMegaMenuOpen(false);
            }}
            className="flex h-11 w-11 items-center justify-center text-slate-950 lg:hidden"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          >
            {menuOpen ? (
              <X size={23} strokeWidth={1.5} />
            ) : (
              <Menu size={23} strokeWidth={1.5} />
            )}
          </button>

          <button
            type="button"
            onClick={() => goTo("/")}
            aria-label="Go to CHARMS home page"
            className="absolute left-1/2 flex -translate-x-1/2 flex-col items-center text-slate-950"
          >
            <div className="flex items-center gap-2">
              {/* <img
                src={logo}
                alt=""
                className="h-7 w-7 object-contain sm:h-8 sm:w-8"
              /> */}

              <span className="font-serif text-[25px] font-semibold leading-none tracking-[0.16em] sm:text-[31px]">
                CHARMS
              </span>
            </div>

            <span className="mt-1 text-[7px] font-bold uppercase tracking-[0.36em] text-primary-800 sm:text-[8px]">
              Premium Fashion Store
            </span>
          </button>

          <div className="ml-auto flex min-w-[116px] items-center justify-end gap-1 sm:gap-2 lg:min-w-[260px]">
            <button
              type="button"
              onClick={() => {
                setSearchOpen(true);
                setMenuOpen(false);
                setProfileOpen(false);
                setMegaMenuOpen(false);
              }}
              className="flex h-10 w-10 items-center justify-center text-slate-900 transition hover:text-primary-800"
              aria-label="Search products"
            >
              <Search size={20} strokeWidth={1.55} />
            </button>

            <button
              type="button"
              onClick={() => goTo("/cart")}
              className="relative flex h-10 w-10 items-center justify-center text-slate-900 transition hover:text-primary-800"
              aria-label={`Shopping bag with ${cartCount} items`}
            >
              <ShoppingBag size={20} strokeWidth={1.55} />

              {cartCount > 0 ? (
                <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-800 px-1 text-[8px] font-extrabold text-white">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              ) : null}
            </button>

            {isCustomerLoading ? (
              <div className="hidden h-8 w-8 animate-pulse bg-slate-100 sm:block" />
            ) : (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    if (!isAuthenticated) {
                      goTo("/signin");
                      return;
                    }

                    setProfileOpen((current) => !current);
                    setMenuOpen(false);
                    setSearchOpen(false);
                    setMegaMenuOpen(false);
                  }}
                  className={`flex h-10 w-10 items-center justify-center transition ${
                    profileOpen
                      ? "bg-primary-800 text-white"
                      : "text-slate-900 hover:text-primary-800"
                  }`}
                  aria-label={isAuthenticated ? "Open account menu" : "Sign in"}
                >
                  <UserRound size={20} strokeWidth={1.55} />
                </button>

                {profileOpen && isAuthenticated ? (
                  <div className="absolute right-0 top-[53px] z-[70] w-72 border border-[#e8dfdb] bg-white p-2 shadow-[0_24px_70px_rgba(34,12,18,0.2)]">
                    <div className="border-b border-[#eee7e4] px-3 py-4">
                      <p className="truncate text-sm font-bold text-slate-950">
                        {customer?.fullName || "CHARMS Customer"}
                      </p>

                      <p className="mt-1 truncate text-xs text-slate-500">
                        {customer?.email || customer?.phone || "My account"}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => goTo("/userprofile")}
                      className="mt-2 flex w-full items-center gap-3 px-3 py-3 text-left text-sm font-semibold text-slate-700 transition hover:bg-[#f9f4f2] hover:text-primary-800"
                    >
                      <UserRound size={16} />
                      My Profile
                    </button>

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-3 py-3 text-left text-sm font-semibold text-red-700 transition hover:bg-red-50"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Desktop category navigation */}
        <div className="hidden border-t border-[#eee7e4] lg:block">
          <nav className="mx-auto flex h-[54px] max-w-[1536px] items-center justify-center gap-8 px-8 xl:px-12">
            <button
              type="button"
              onClick={() => goTo("/search/all")}
              className="text-[10px] font-extrabold uppercase tracking-[0.17em] text-primary-800 transition hover:text-primary-950"
            >
              Sale
            </button>

            <button
              type="button"
              onClick={() => goTo("/search/all?sort=newest")}
              className="text-[10px] font-bold uppercase tracking-[0.17em] text-slate-700 transition hover:text-primary-800"
            >
              New arrivals
            </button>

            {isLoadingCategories
              ? Array.from({ length: 5 }).map((_, index) => (
                  <span
                    key={index}
                    className="h-3 w-20 animate-pulse bg-slate-100"
                  />
                ))
              : navCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => goTo(categoryPath(category))}
                    className={`relative h-[54px] text-[10px] font-bold uppercase tracking-[0.17em] transition ${
                      isCategoryActive(category)
                        ? "text-primary-800"
                        : "text-slate-700 hover:text-primary-800"
                    }`}
                  >
                    {category.name}

                    <span
                      className={`absolute inset-x-0 bottom-0 h-px bg-primary-800 transition-transform duration-300 ${
                        isCategoryActive(category) ? "scale-x-100" : "scale-x-0"
                      }`}
                    />
                  </button>
                ))}

            <button
              type="button"
              onMouseEnter={() => setMegaMenuOpen(true)}
              onClick={() => {
                setMegaMenuOpen((current) => !current);
                setProfileOpen(false);
              }}
              className={`flex h-[54px] items-center gap-1 text-[10px] font-bold uppercase tracking-[0.17em] transition ${
                megaMenuOpen
                  ? "text-primary-800"
                  : "text-slate-700 hover:text-primary-800"
              }`}
            >
              Explore
              <ChevronDown
                size={14}
                className={`transition-transform ${
                  megaMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </nav>
        </div>

        {/* Full visual mega menu */}
        {megaMenuOpen ? (
          <div
            onMouseLeave={() => setMegaMenuOpen(false)}
            className="absolute left-0 right-0 top-full z-50 hidden border-b border-[#e8dfdb] bg-[#fcfbfa] shadow-[0_26px_55px_rgba(34,12,18,0.12)] lg:block"
          >
            <div className="mx-auto grid max-w-[1536px] grid-cols-[245px_1fr] gap-10 px-12 py-10">
              <div className="border-r border-[#e6dcda] pr-9">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.23em] text-primary-800">
                  Discover CHARMS
                </p>

                <h2 className="mt-4 font-serif text-4xl leading-[1.05] text-slate-950">
                  The season,
                  <br />
                  carefully edited.
                </h2>

                <button
                  type="button"
                  onClick={() => goTo("/search/all")}
                  className="mt-7 inline-flex items-center gap-2 border-b border-slate-950 pb-1 text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-950 transition hover:border-primary-800 hover:text-primary-800"
                >
                  Shop all collections
                  <ArrowUpRight size={14} />
                </button>
              </div>

              {isLoadingCategories ? (
                <div className="grid grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={index}
                      className="aspect-[3/4] animate-pulse bg-slate-100"
                    />
                  ))}
                </div>
              ) : megaCategories.length > 0 ? (
                <div className="grid grid-cols-4 gap-4">
                  {megaCategories.slice(0, 4).map((category) => {
                    const imageUrl = getImageUrl(
                      category.image || category.imageKey,
                    );

                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => goTo(categoryPath(category))}
                        className="group relative aspect-[3/4] overflow-hidden bg-[#eadfdb] text-left"
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={category.name}
                            className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-110"
                          />
                        ) : null}

                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />

                        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                          <p className="text-[9px] font-bold uppercase tracking-[0.17em] text-white/70">
                            Shop category
                          </p>

                          <div className="mt-2 flex items-end justify-between gap-2">
                            <span className="font-serif text-2xl leading-none">
                              {category.name}
                            </span>

                            <ArrowUpRight
                              size={17}
                              className="mb-0.5 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center border border-dashed border-[#dfd3cf] p-10 text-sm text-slate-500">
                  Categories will appear here once available online.
                </div>
              )}
            </div>
          </div>
        ) : null}
      </header>

      {/* Full-screen search */}
      {searchOpen ? (
        <div className="fixed inset-0 z-[100] bg-[#fcfbfa]">
          <div className="mx-auto flex h-full max-w-[1536px] flex-col px-5 py-6 sm:px-8 lg:px-12 lg:py-10">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => goTo("/")}
                className="flex items-center gap-2 text-slate-950"
              >
                <img src={logo} alt="" className="h-7 w-7 object-contain" />
                <span className="font-serif text-2xl tracking-[0.14em]">
                  CHARMS
                </span>
              </button>

              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="flex h-12 w-12 items-center justify-center border border-slate-950 text-slate-950 transition hover:bg-slate-950 hover:text-white"
                aria-label="Close search"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>

            <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center">
              <p className="text-center text-[10px] font-extrabold uppercase tracking-[0.25em] text-primary-800">
                Search the collection
              </p>

              <div className="mt-6 flex items-center border-b-2 border-slate-950 pb-4">
                <Search
                  size={25}
                  strokeWidth={1.45}
                  className="mr-4 shrink-0 text-primary-800"
                />

                <input
                  ref={searchInputRef}
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleSearch();
                  }}
                  placeholder="What are you looking for?"
                  className="min-w-0 flex-1 bg-transparent font-serif text-2xl text-slate-950 outline-none placeholder:text-slate-400 sm:text-4xl"
                />

                <button
                  type="button"
                  onClick={handleSearch}
                  className="ml-4 bg-primary-800 px-5 py-3 text-[10px] font-extrabold uppercase tracking-[0.16em] text-white transition hover:bg-primary-950 sm:px-7"
                >
                  Search
                </button>
              </div>

              <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3">
                {["New Arrivals", "Sale", "Shirts", "Denim", "Footwear"].map(
                  (item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() =>
                        goTo(`/search/${encodeURIComponent(item)}`)
                      }
                      className="border-b border-[#cfc2be] pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 transition hover:border-primary-800 hover:text-primary-800"
                    >
                      {item}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Full-height mobile navigation */}
      {menuOpen ? (
        <div className="fixed inset-0 z-[90] bg-[#fcfbfa] lg:hidden">
          <div className="flex h-full flex-col">
            <div className="flex h-[74px] items-center justify-between border-b border-[#e8dfdb] px-5">
              <button
                type="button"
                onClick={() => goTo("/")}
                className="flex items-center gap-2 text-slate-950"
              >
                <img src={logo} alt="" className="h-7 w-7 object-contain" />
                <span className="font-serif text-2xl tracking-[0.14em]">
                  CHARMS
                </span>
              </button>

              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="flex h-11 w-11 items-center justify-center border border-slate-950 text-slate-950"
                aria-label="Close navigation"
              >
                <X size={21} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-7">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-primary-800">
                Browse the collection
              </p>

              <div className="mt-5 border-t border-[#e8dfdb]">
                <button
                  type="button"
                  onClick={() => goTo("/search/all")}
                  className="flex w-full items-center justify-between border-b border-[#e8dfdb] py-5 font-serif text-3xl text-primary-800"
                >
                  Sale
                  <ArrowUpRight size={19} />
                </button>

                <button
                  type="button"
                  onClick={() => goTo("/search/all?sort=newest")}
                  className="flex w-full items-center justify-between border-b border-[#e8dfdb] py-5 font-serif text-3xl text-slate-950"
                >
                  New Arrivals
                  <ArrowUpRight size={19} />
                </button>

                <button type="button" onClick={() => goTo("/gift-cards")} className="flex w-full items-center justify-between border-b border-[#e8dfdb] py-5 font-serif text-3xl text-primary-800">
                  Gift cards <ArrowUpRight size={19} />
                </button>
                {mobileCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => goTo(categoryPath(category))}
                    className="flex w-full items-center justify-between border-b border-[#e8dfdb] py-4 text-left text-lg font-medium text-slate-800"
                  >
                    {category.name}
                    <ArrowUpRight size={16} className="text-primary-800" />
                  </button>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-2 gap-px bg-[#e8dfdb]">
                <button
                  type="button"
                  onClick={() => goTo("/cart")}
                  className="flex min-h-[116px] flex-col justify-between bg-white p-5 text-left"
                >
                  <ShoppingBag size={20} className="text-primary-800" />
                  <span className="text-sm font-bold text-slate-950">
                    Bag ({cartCount})
                  </span>
                </button>

                {isAuthenticated ? (
                  <button
                    type="button"
                    onClick={() => goTo("/userprofile")}
                    className="flex min-h-[116px] flex-col justify-between bg-white p-5 text-left"
                  >
                    <UserRound size={20} className="text-primary-800" />
                    <span className="text-sm font-bold text-slate-950">
                      My Account
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => goTo("/signin")}
                    className="flex min-h-[116px] flex-col justify-between bg-primary-800 p-5 text-left text-white"
                  >
                    <UserRound size={20} />
                    <span className="text-sm font-bold">Sign In</span>
                  </button>
                )}
              </div>

              {isAuthenticated ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="mt-5 flex items-center gap-3 text-sm font-bold text-red-700"
                >
                  <LogOut size={17} />
                  Logout
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
